// ---------------------------------------------------------------------------
// "This material is already on its way" — the pure rules behind the Stock
// tab's join box (contract v9).
//
// A booking with no stock on site has one thing to look for: the site's OPEN
// delivery order for the same material. The one-unsynced-order rule keeps at
// most one of those per material, so there is never a list to choose from —
// the checkout either adds the booking to that order or, when it cannot,
// says what has to change (a new order would hit the same rule).
//
// A task already on that order holds a SHARE of its line (contract v13):
// what it put there when the order was raised plus every join since. A claim
// that outgrows the share adds the difference the same way.
//
// No React, no query: `useSiteStockContext` feeds these the site's deliveries.
// ---------------------------------------------------------------------------

import type { Delivery, IDeliveryLine } from '@interfaces/ILogistics';
import type { ITaskStockClaim } from '@interfaces/IScheduler';
import { lineFor, orderLinesFor } from '../../LogisticsPage/lines';
import { sameUnit } from '@d-orca/shared/client/logistics';
import { round1 } from './claimEvaluation';

/** This task's own booking on an open order for a material: the order, and
 *  the task's share of its line (`IDeliverySource.quantity`). */
export interface IOwnBooking {
  order: Delivery;
  share: number;
}

/** Whether the task's share on its order covers what the claim still needs
 *  — to the tenth amounts are recorded in, so 126.6 booked against a 126.6
 *  need is not a shortfall of a float artefact. No booking covers nothing. */
export function bookingCovers(booking: IOwnBooking | null, need: number): boolean {
  return booking !== null && need <= booking.share + 0.05;
}

/** Sorts after every real date, so an order with no date of its own is the
 *  last one considered rather than the first. */
const FAR_FUTURE = '9999-12-31';

/** How near an order is: its ETA first, then the day it was requested. */
const nearness = (order: Delivery): string =>
  `${order.dueDate ?? FAR_FUTURE}|${order.requestedDate ?? FAR_FUTURE}`;

/**
 * The site's open (not yet arrived) delivery order carrying a LINE for
 * `materialName`, or null. Matched by the same fold the stock and delivery
 * dialogs use, so "Concrete B-30" and "concrete b-30" are one material.
 *
 * The one-unsynced-line rule means at most one can qualify; should the data
 * ever say otherwise, the nearest arrival wins — that is the one a task
 * waiting on the material cares about.
 */
export function openOrderFor(deliveries: Delivery[], materialName: string): Delivery | null {
  let best: Delivery | null = null;
  for (const { order } of orderLinesFor(deliveries, materialName)) {
    if (order.deliveredAt !== null) continue;
    if (best === null || nearness(order) < nearness(best)) best = order;
  }
  return best;
}

/** An open order a booking may join, and the LINE it joins. The line's unit
 *  must be known, since agreeing on the measure is what makes the two
 *  quantities addable. */
export type IJoinableOrder = Delivery & { line: IDeliveryLine & { unit: string } };

/** Why a claim may not add to the open order carrying its material: the line
 *  measures it in another unit, records no unit at all, or the claim's name
 *  was typed rather than picked (contract v14 — the checkout's "Needs a
 *  change" group names each). */
export type JoinRefusal = 'unit' | 'noUnit' | 'freeText';

/**
 * The order this claim may add to, with the line it adds to — or why not.
 *
 * `order` is the one the caller found BY this material (`bookingFor` or
 * `openOrderFor`, both through `orderLinesFor`), so it carries the line.
 *
 * A free-text booking may not join ANOTHER task's order: its name was typed,
 * not picked, so matching it by name alone would put a task's quantity on
 * someone else's delivery on a spelling coincidence. The task's `own` order
 * is matched by this task's source on the line, not by spelling, so that
 * exclusion does not apply there — the unit rule still does.
 *
 * A unit that is not the LINE's is a refusal, not a conversion: the backend
 * would answer `UNIT_MISMATCH`.
 */
export function joinableOrder(
  order: Delivery,
  claim: ITaskStockClaim,
  own: boolean,
): IJoinableOrder | JoinRefusal {
  if (!own && !claim.fromCad) return 'freeText';
  const line = lineFor(order, claim.materialName)!;
  const { unit } = line;
  if (unit === null) return 'noUnit';
  if (!sameUnit(unit, claim.unit)) return 'unit';
  // Re-stated rather than cast: checking a property narrows the property, not
  // the object, so this is what carries "this line HAS a unit" into the type
  // the review renders — no `?? ''` fallback for a case that cannot happen.
  return { ...order, line: { ...line, unit } };
}

/**
 * The order's ETA when it falls AFTER the task is due to start — the box
 * keeps its one action but says so in amber — and null when it does not.
 *
 * The day itself rather than a boolean: the late line names the date, and a
 * boolean would leave the caller re-checking `dueDate` for null against a
 * condition that already settled it.
 *
 * An undated task (the plan stores '' for a missing date) has no start to be
 * late for.
 */
export function etaAfterTaskStart(order: Delivery, taskStart: string): string | null {
  const { dueDate } = order;
  return dueDate !== null && taskStart !== '' && dueDate > taskStart ? dueDate : null;
}

/** What the LINE will be for once `amount` is added to it, rounded like every
 *  other recorded amount — two one-decimal numbers still sum to a float
 *  artefact. A line that never recorded an amount contributes nothing. */
export function quantityAfterJoin(line: IDeliveryLine, amount: number): number {
  return round1((line.quantity ?? 0) + amount);
}
